import { useCallback, useEffect, useState } from 'react';

import { Col, Input, PageHeader, Row, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useHistory, useLocation } from 'react-router';
import { T } from 'react-translator-component';

import * as S from './styled';

import { getVirtualGroupsChargeboxes } from '../../../../hooks/virtualgroup';
import { Pagination } from '../../../molecules/Pagination/Pagination';

const { Search } = Input;

const VirtualGroupChargeboxes = () => {
  const history = useHistory();
  const { state } = useLocation<any>();
  const [loading, setLoading] = useState(false);
  const [chargeboxesSelected, setChargeboxesSelected] = useState<any>(
    state.payload.charge_boxs,
  );
  const [chargeBoxSearchValue, setChargeBoxSearchValue] = useState('');

  const columns: ColumnsType<any> = [
    {
      title: 'Carregador',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'ID',
      dataIndex: 'charge_box_code',
      key: 'charge_box_code',
    },
    {
      title: 'Configurações avançadas',
      dataIndex: 'has_config_tec',
      key: 'has_config_tec',
      align: 'center',
      render: (value) => <S.Bullet active={value} />,
    },
  ];

  const rowSelection = {
    onSelect(select) {
      setChargeboxesSelected((prevState) => {
        const itemIndex = prevState.findIndex(
          (item) =>
            item.charge_box_id === select.charge_box_id && item.isSelected,
        );

        if (itemIndex > -1) {
          return prevState.filter(
            (item) => item.charge_box_id !== select.charge_box_id,
          );
        }

        return [
          ...prevState,
          {
            ...select,
            isSelected: true,
          },
        ];
      });
    },
  };

  const handleEndSelection = () => {
    history.push({
      pathname: state.origin,
      state: {
        payload: {
          ...state.payload,
          charge_boxs: chargeboxesSelected.map((chargebox) => ({
            charge_box_id: chargebox.charge_box_id,
            charge_box_code: chargebox.charge_box_code,
            key: chargebox.key,
          })),
        },
      },
    });
  };

  const tryToGetVirtualGroupsChargeboxes = useCallback(
    async (page: number = 1, pageSize: number = 10, searchValue?: string) => {
      if (!state?.payload) {
        return history.push('/virtual-groups/create');
      }

      setLoading(true);

      try {
        const { data } = await getVirtualGroupsChargeboxes(
          page,
          pageSize,
          searchValue,
        );

        setChargeboxes((value) => ({
          ...value,
          data: data.content.map((value, key) => ({
            ...value,
            key: `chargebox-${key}`,
          })),
          pagination: {
            page,
            pageSize,
            total: data.totalElements,
          },
        }));
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    },
    [history, state?.payload],
  );

  const [chargeboxes, setChargeboxes] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetVirtualGroupsChargeboxes,
    pagination: { page: 1, pageSize: 10 },
  });

  useEffect(() => {
    (async () => {
      await tryToGetVirtualGroupsChargeboxes();
    })();
  }, [tryToGetVirtualGroupsChargeboxes]);

  return (
    <S.Wrapper>
      <PageHeader onBack={history.goBack} title={T('Select chargers')} />

      <S.Blank>
        <S.Legend>
          Selecione os pontos de recarga que devem fazer parte da configuração{' '}
          <strong>{state?.payload?.name}</strong>
        </S.Legend>

        <Search
          className='search'
          placeholder={'Procure pelo ID ou descrição do carregador'}
          loading={false}
          size='large'
          value={chargeBoxSearchValue}
          onSearch={() =>
            tryToGetVirtualGroupsChargeboxes(
              chargeboxes?.pagination?.page,
              chargeboxes?.pagination?.pageSize,
              chargeBoxSearchValue,
            )
          }
          onChange={(e) => setChargeBoxSearchValue(e.target.value)}
          style={{ width: '50%', marginBottom: 16 }}
        />

        <Skeleton
          loading={loading}
          active
          paragraph={{
            rows: 5,
            width: '100%',
          }}
          title={false}
        >
          <S.TableWrapper>
            <Table
              rowSelection={{
                hideSelectAll: true,
                selectedRowKeys: chargeboxesSelected.map(
                  (chargebox) => chargebox.charge_box_code,
                ),
                type: 'checkbox',
                ...rowSelection,
              }}
              dataSource={chargeboxes?.data}
              columns={columns}
              rowKey='charge_box_code'
              pagination={false}
              locale={{ emptyText: 'Nenhum carregador encontrado' }}
            />
            <Pagination
              currentPage={chargeboxes?.pagination?.page}
              pageSize={chargeboxes?.pagination?.pageSize}
              totalCount={chargeboxes?.pagination?.total}
              onPageChange={(page: number, pageSize: number | undefined) => {
                chargeboxes.request(page, pageSize);
              }}
            />
          </S.TableWrapper>

          <Row justify='space-between' align='middle'>
            <Col md={12}>
              <S.ChargeboxesSelectedText>
                {chargeboxesSelected?.length} pontos de recarga selecionado
                {chargeboxesSelected.length >= 1 ? 's' : ''}
              </S.ChargeboxesSelectedText>
            </Col>
            <Col md={12} style={{ textAlign: 'right' }}>
              <S.Button
                key='submit'
                size='large'
                type='primary'
                onClick={handleEndSelection}
              >
                Concluir seleção
              </S.Button>
            </Col>
          </Row>
        </Skeleton>
      </S.Blank>
    </S.Wrapper>
  );
};

export default VirtualGroupChargeboxes;
